import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  UserPlus,
  MoreVertical,
  Edit2,
  Trash2,
  Shield,
  Ban,
  ChevronDown,
  ChevronUp,
  User,
  Monitor,
  Smartphone,
} from 'lucide-react';
import {
  AdminClientApi,
  ClientUserInterface,
  GetUsersInputInterface,
  LoginAsUserInputInterface,
  LoginAsUserResponseInterface,
} from '../../Admin-ClientLibrary';
import { getApiUrl } from '../../config/api';
import './Users.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  // This would typically get the token from localStorage, context, or wherever you store it
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const Users: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [users, setUsers] = useState<ClientUserInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Page history stack for cursor-based pagination
  const [pageHistory, setPageHistory] = useState<Array<{ cursor: string | null; pageNum: number }>>(
    [{ cursor: null, pageNum: 1 }]
  );

  // Pagination and ordering state
  const [orderBy, setOrderBy] = useState<string>(searchParams.get('orderBy') || 'id');
  const [orderDirection, setOrderDirection] = useState<string>(
    searchParams.get('orderDirection') || 'DESC'
  );

  // Initialize filters from URL parameters
  const [filters, setFilters] = useState({
    userType: searchParams.get('userType') || '',
    birthdateFrom: searchParams.get('birthdateFrom') || '',
    birthdateTo: searchParams.get('birthdateTo') || '',
    hasBeliefOnThought: searchParams.get('hasBeliefOnThought') || '',
    education: searchParams.get('education') || '',
    political: searchParams.get('political') || '',
    sex: searchParams.get('sex') || '',
    country: searchParams.get('country') || '',
    state: searchParams.get('state') || '',
    city: searchParams.get('city') || '',
    religion: searchParams.get('religion') || '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    userType: searchParams.get('userType') || '',
    birthdateFrom: searchParams.get('birthdateFrom') || '',
    birthdateTo: searchParams.get('birthdateTo') || '',
    hasBeliefOnThought: searchParams.get('hasBeliefOnThought') || '',
    education: searchParams.get('education') || '',
    political: searchParams.get('political') || '',
    sex: searchParams.get('sex') || '',
    country: searchParams.get('country') || '',
    state: searchParams.get('state') || '',
    city: searchParams.get('city') || '',
    religion: searchParams.get('religion') || '',
  });

  // Add state for dropdown menus
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  const limit = 20;

  // Function to update URL parameters
  const updateUrlParams = (params: Record<string, string | number | undefined>): void => {
    const newParams = new URLSearchParams();

    // Add all current search params first
    searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });

    // Update with new params
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  // Function to fetch users from API with cursor-based pagination
  const fetchUsers = async (
    direction: 'first' | 'next' | 'previous' = 'first',
    cursor?: string,
    search: string = '',
    filterParams = filters,
    order = orderBy,
    orderDir = orderDirection,
    shouldGetCount: boolean = false
  ): Promise<void> => {
    console.log('fetchUsers called with:', {
      direction,
      cursor,
      search,
      limit,
      filterParams,
      order,
      orderDir,
      shouldGetCount,
    });

    try {
      setLoading(true);
      setError(null);

      console.log('About to call adminApi.getUsers...');
      const apiParams: GetUsersInputInterface = {
        limit,
        search,
        orderBy: order,
        orderDirection: orderDir,
        getCount: shouldGetCount,
      };

      // Add cursor parameters based on direction and order
      if (direction === 'next' && cursor) {
        if (order === 'id') {
          if (orderDir === 'DESC') {
            apiParams.beforeId = parseInt(cursor);
          } else {
            apiParams.afterId = parseInt(cursor);
          }
        } else if (order === 'createdAt') {
          if (orderDir === 'DESC') {
            apiParams.beforeCreatedDate = cursor;
          } else {
            apiParams.afterCreatedDate = cursor;
          }
        }
      } else if (direction === 'previous' && cursor) {
        if (order === 'id') {
          if (orderDir === 'DESC') {
            apiParams.afterId = parseInt(cursor);
          } else {
            apiParams.beforeId = parseInt(cursor);
          }
        } else if (order === 'createdAt') {
          if (orderDir === 'DESC') {
            apiParams.afterCreatedDate = cursor;
          } else {
            apiParams.beforeCreatedDate = cursor;
          }
        }
      }

      // Add filter parameters if they have values
      if (filterParams.userType) {
        apiParams.userType = filterParams.userType;
      }
      if (filterParams.birthdateFrom) {
        apiParams.birthdateFrom = filterParams.birthdateFrom;
      }
      if (filterParams.birthdateTo) {
        apiParams.birthdateTo = filterParams.birthdateTo;
      }
      if (filterParams.hasBeliefOnThought) {
        apiParams.hasBeliefOnThought = parseInt(filterParams.hasBeliefOnThought);
      }
      if (filterParams.education) {
        apiParams.education = filterParams.education;
      }
      if (filterParams.political) {
        apiParams.political = filterParams.political;
      }
      if (filterParams.sex) {
        apiParams.sex = filterParams.sex;
      }
      if (filterParams.country) {
        apiParams.country = filterParams.country;
      }
      if (filterParams.state) {
        apiParams.state = filterParams.state;
      }
      if (filterParams.city) {
        apiParams.city = filterParams.city;
      }
      if (filterParams.religion) {
        apiParams.religion = filterParams.religion;
      }

      console.log('API parameters:', apiParams);
      const response = await adminApi.getUsers(apiParams);
      console.log('API response received:', response);

      // Check if no results and show alert for navigation attempts
      if (response.users.length === 0 && (direction === 'next' || direction === 'previous')) {
        alert('No results');
        return;
      }

      setUsers(response.users);

      // Only update total count if it was requested and returned
      if (shouldGetCount && response.total !== undefined) {
        setTotalUsers(response.total);
      }

      // Update page history and current page based on direction
      if (direction === 'first') {
        // Reset to first page
        setCurrentPage(1);
        setPageHistory([{ cursor: null, pageNum: 1 }]);
      } else if (direction === 'next' && response.users.length > 0) {
        // Add current page to history and move to next page
        const nextPageNum = currentPage + 1;
        const lastItem = response.users[response.users.length - 1];
        const nextCursor =
          order === 'id' ? lastItem.account_id.toString() : lastItem.createdAt.toISOString();

        setCurrentPage(nextPageNum);
        setPageHistory((prev) => [...prev, { cursor: nextCursor, pageNum: nextPageNum }]);
      } else if (direction === 'previous') {
        // Move back in history
        setPageHistory((prev) => {
          const newHistory = [...prev];
          newHistory.pop(); // Remove current page
          const previousPage = newHistory[newHistory.length - 1];
          if (previousPage) {
            setCurrentPage(previousPage.pageNum);
          }
          return newHistory;
        });
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        statusCode: err.statusCode,
        details: err.details,
      });
      setError(err.message || 'Failed to fetch users');
      // Fallback to mock data if API fails
      console.log('Falling back to mock data');
      const mockUsers = getMockUsers();
      setUsers(mockUsers);
      setTotalUsers(mockUsers.length);
      setCurrentPage(1);
      setPageHistory([{ cursor: null, pageNum: 1 }]);
    } finally {
      setLoading(false);
    }
  };

  // Mock data fallback
  const getMockUsers = (): ClientUserInterface[] => [
    {
      account_id: 1,
      name: 'John Doe',
      email: 'john@example.com',
      type: 'premium',
      createdAt: new Date('2023-01-15'),
      updatedAt: new Date('2023-01-15'),
      deletedAt: new Date('2023-01-15'),
      city: 'New York',
      ageCategory: 'Adult',
    },
    {
      account_id: 2,
      name: 'Jane Smith',
      email: 'jane@example.com',
      type: 'basic',
      createdAt: new Date('2023-02-20'),
      updatedAt: new Date('2023-02-20'),
      deletedAt: new Date('2023-02-20'),
      city: 'Los Angeles',
      ageCategory: 'Adult',
    },
    {
      account_id: 3,
      name: 'Mike Johnson',
      email: 'mike@example.com',
      type: 'premium',
      createdAt: new Date('2023-03-10'),
      updatedAt: new Date('2023-03-10'),
      deletedAt: new Date('2023-03-10'),
      city: 'Chicago',
      ageCategory: 'Adult',
    },
  ];

  // Effect to respond to URL parameter changes
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlOrderBy = searchParams.get('orderBy') || 'id';
    const urlOrderDirection = searchParams.get('orderDirection') || 'DESC';
    const urlFilters = {
      userType: searchParams.get('userType') || '',
      birthdateFrom: searchParams.get('birthdateFrom') || '',
      birthdateTo: searchParams.get('birthdateTo') || '',
      hasBeliefOnThought: searchParams.get('hasBeliefOnThought') || '',
      education: searchParams.get('education') || '',
      political: searchParams.get('political') || '',
      sex: searchParams.get('sex') || '',
      country: searchParams.get('country') || '',
      state: searchParams.get('state') || '',
      city: searchParams.get('city') || '',
      religion: searchParams.get('religion') || '',
    };

    // Update local state from URL
    setSearchTerm(urlSearch);
    setOrderBy(urlOrderBy);
    setOrderDirection(urlOrderDirection);
    setFilters(urlFilters);
    setAppliedFilters(urlFilters);

    // Reset page history when URL parameters change (filters, order, search, etc.)
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);

    // Fetch data with URL parameters (always get count on URL changes since this indicates initial load or filter changes)
    fetchUsers('first', undefined, urlSearch, urlFilters, urlOrderBy, urlOrderDirection, true);
  }, [searchParams]);

  // Add click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('.action-dropdown')) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection,
      userType: filters.userType,
      birthdateFrom: filters.birthdateFrom,
      birthdateTo: filters.birthdateTo,
      hasBeliefOnThought: filters.hasBeliefOnThought,
      education: filters.education,
      political: filters.political,
      sex: filters.sex,
      country: filters.country,
      state: filters.state,
      city: filters.city,
      religion: filters.religion,
    });
  };

  const handleSelectUser = (userId: number): void => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = (): void => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map((user) => user.account_id)));
    }
  };

  const getRoleColor = (type: string): string => {
    switch (type) {
      case 'free':
        return 'free';
      case 'beta':
        return 'beta';
      case 'premium':
        return 'premium';
      default:
        return 'free';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  const handleNextPage = (): void => {
    if (users.length > 0) {
      const lastItem = users[users.length - 1];
      const cursor =
        orderBy === 'id' ? lastItem.account_id.toString() : lastItem.createdAt.toISOString();
      fetchUsers('next', cursor, searchTerm, appliedFilters, orderBy, orderDirection, false);
    }
  };

  const handlePreviousPage = (): void => {
    if (pageHistory.length > 1) {
      // Get the cursor from the previous page in history
      const previousPageIndex = pageHistory.length - 2; // Go back one page
      const previousPage = pageHistory[previousPageIndex];
      fetchUsers(
        'previous',
        previousPage.cursor || undefined,
        searchTerm,
        appliedFilters,
        orderBy,
        orderDirection,
        false
      );
    }
  };

  const handleOrderByChange = (newOrderBy: string): void => {
    updateUrlParams({
      search: searchTerm,
      orderBy: newOrderBy,
      orderDirection,
      userType: appliedFilters.userType,
      birthdateFrom: appliedFilters.birthdateFrom,
      birthdateTo: appliedFilters.birthdateTo,
      hasBeliefOnThought: appliedFilters.hasBeliefOnThought,
      education: appliedFilters.education,
      political: appliedFilters.political,
      sex: appliedFilters.sex,
      country: appliedFilters.country,
      state: appliedFilters.state,
      city: appliedFilters.city,
      religion: appliedFilters.religion,
    });
  };

  const handleOrderDirectionChange = (newDirection: string): void => {
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection: newDirection,
      userType: appliedFilters.userType,
      birthdateFrom: appliedFilters.birthdateFrom,
      birthdateTo: appliedFilters.birthdateTo,
      hasBeliefOnThought: appliedFilters.hasBeliefOnThought,
      education: appliedFilters.education,
      political: appliedFilters.political,
      sex: appliedFilters.sex,
      country: appliedFilters.country,
      state: appliedFilters.state,
      city: appliedFilters.city,
      religion: appliedFilters.religion,
    });
  };

  const handleFilterChange = (filterName: string, value: string): void => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
  };

  const applyFilters = (): void => {
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection,
      userType: filters.userType,
      birthdateFrom: filters.birthdateFrom,
      birthdateTo: filters.birthdateTo,
      hasBeliefOnThought: filters.hasBeliefOnThought,
      education: filters.education,
      political: filters.political,
      sex: filters.sex,
      country: filters.country,
      state: filters.state,
      city: filters.city,
      religion: filters.religion,
    });
    setShowFilters(false);
  };

  // Calculate total pages
  const totalPages = totalUsers > 0 ? Math.ceil(totalUsers / limit) : 1;

  const getFilterSummary = (): string => {
    const activeParts: string[] = [];

    if (appliedFilters.userType) {
      activeParts.push(`Type: ${appliedFilters.userType}`);
    }

    if (appliedFilters.birthdateFrom || appliedFilters.birthdateTo) {
      if (appliedFilters.birthdateFrom && appliedFilters.birthdateTo) {
        activeParts.push(`Born: ${appliedFilters.birthdateFrom} to ${appliedFilters.birthdateTo}`);
      } else if (appliedFilters.birthdateFrom) {
        activeParts.push(`Born after: ${appliedFilters.birthdateFrom}`);
      } else {
        activeParts.push(`Born before: ${appliedFilters.birthdateTo}`);
      }
    }

    if (appliedFilters.hasBeliefOnThought) {
      activeParts.push(`Have belief on thought: ${appliedFilters.hasBeliefOnThought}`);
    }

    if (appliedFilters.education) {
      activeParts.push(`Education: ${appliedFilters.education}`);
    }

    if (appliedFilters.political) {
      activeParts.push(`Political: ${appliedFilters.political}`);
    }

    if (appliedFilters.sex) {
      activeParts.push(`Sex: ${appliedFilters.sex}`);
    }

    if (appliedFilters.country) {
      activeParts.push(`Country: ${appliedFilters.country}`);
    }

    if (appliedFilters.state) {
      activeParts.push(`State: ${appliedFilters.state}`);
    }

    if (appliedFilters.city) {
      activeParts.push(`City: ${appliedFilters.city}`);
    }

    if (appliedFilters.religion) {
      activeParts.push(`Religion: ${appliedFilters.religion}`);
    }

    if (activeParts.length === 0) {
      return `Ordered by ${
        orderBy === 'id' ? 'ID' : 'Created Date'
      } (${orderDirection}). Total: ${totalUsers}`;
    }

    return `${activeParts.join(', ')} | Ordered by ${
      orderBy === 'id' ? 'ID' : 'Created Date'
    } (${orderDirection}). Total: ${totalUsers}`;
  };

  const calculateAgeFromDate = (dateString: string): number => {
    // Parse the date string manually to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const birthDate = new Date(year, month - 1, day); // month is 0-indexed
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const clearFilters = (): void => {
    updateUrlParams({
      orderBy,
      orderDirection,
      userType: '',
      birthdateFrom: '',
      birthdateTo: '',
      hasBeliefOnThought: '',
      education: '',
      political: '',
      sex: '',
      country: '',
      state: '',
      city: '',
      religion: '',
    });
    setShowFilters(false);
  };

  const setAgeCategory = (category: string): void => {
    const today = new Date();
    let fromDate = '';
    let toDate = '';

    switch (category) {
      case '<20':
        // People under 20: born after the date when they would turn 20
        const date20YearsAgo = new Date(today);
        date20YearsAgo.setFullYear(today.getFullYear() - 20);
        date20YearsAgo.setDate(date20YearsAgo.getDate() + 1); // Day after they turn 20

        fromDate = date20YearsAgo.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;

      case '20-39':
        // People 20-39: born between when they turn 40 and when they turn 20
        const date40YearsAgo = new Date(today);
        date40YearsAgo.setFullYear(today.getFullYear() - 40);
        date40YearsAgo.setDate(date40YearsAgo.getDate() + 1); // Day after they turn 40

        const date20YearsAgoEnd = new Date(today);
        date20YearsAgoEnd.setFullYear(today.getFullYear() - 20);

        fromDate = date40YearsAgo.toISOString().split('T')[0];
        toDate = date20YearsAgoEnd.toISOString().split('T')[0];
        break;

      case '40-59':
        // People 40-59: born between when they turn 60 and when they turn 40
        const date60YearsAgo = new Date(today);
        date60YearsAgo.setFullYear(today.getFullYear() - 60);
        date60YearsAgo.setDate(date60YearsAgo.getDate() + 1); // Day after they turn 60

        const date40YearsAgoEnd = new Date(today);
        date40YearsAgoEnd.setFullYear(today.getFullYear() - 40);

        fromDate = date60YearsAgo.toISOString().split('T')[0];
        toDate = date40YearsAgoEnd.toISOString().split('T')[0];
        break;

      case '60+':
        // People 60+: born before when they turn 60
        const date60YearsAgoMax = new Date(today);
        date60YearsAgoMax.setFullYear(today.getFullYear() - 60);

        fromDate = '1900-01-01';
        toDate = date60YearsAgoMax.toISOString().split('T')[0];
        break;
    }

    const newFilters = {
      ...filters,
      birthdateFrom: fromDate,
      birthdateTo: toDate,
    };
    setFilters(newFilters);
    // Update URL parameters to trigger data fetch
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection,
      userType: filters.userType,
      birthdateFrom: fromDate,
      birthdateTo: toDate,
      hasBeliefOnThought: filters.hasBeliefOnThought,
      education: filters.education,
      political: filters.political,
      sex: filters.sex,
      country: filters.country,
      state: filters.state,
      city: filters.city,
      religion: filters.religion,
    });
  };

  const handleRowClick = (userId: number, event: React.MouseEvent): void => {
    // Prevent navigation if clicking on checkbox, button, or action elements
    const target = event.target as HTMLElement;
    if (
      (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') ||
      target.tagName === 'BUTTON' ||
      target.closest('button') ||
      target.closest('.actions')
    ) {
      return;
    }

    if ((event.target as HTMLElement).closest('.actions')) {
      return;
    }

    navigate(`/users/${userId}`);
  };

  const handleLoginAsUserWeb = async (userId: number): Promise<void> => {
    setOpenDropdown(null); // Close dropdown
    try {
      const response = await adminApi.loginAsUser({ userId });
      if (response.session) {
        const session = response.session;
        // Always open web version in new tab
        window.open(`https://epist-v2.vercel.app/login-as-user?session=${session}`, '_blank');
      } else {
        alert(`Failed to login as user: ${response.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error logging in as user (web):', error);
      alert(`Error: ${error.message || 'Failed to login as user'}`);
    }
  };

  const handleLoginAsUserMobile = async (userId: number): Promise<void> => {
    setOpenDropdown(null); // Close dropdown
    try {
      const response = await adminApi.loginAsUser({ userId });
      if (response.session) {
        const session = response.session;
        // Always redirect to mobile app (defaulting to iOS, but you can make this configurable)
        const platform = 'ios'; // or make this a dropdown/preference
        window.location.href = `exp://u.expo.dev/60cc337f-2d57-4327-84f1-ad98a5600de1/--/login-as-user?channel-name=preview&runtime-version=1.0.0&platform=${platform}&session=${session}`;
      } else {
        alert(`Failed to login as user: ${response.message || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error logging in as user (mobile):', error);
      alert(`Error: ${error.message || 'Failed to login as user'}`);
    }
  };

  const toggleDropdown = (userId: number, event: React.MouseEvent): void => {
    event.stopPropagation();
    setOpenDropdown(openDropdown === userId ? null : userId);
  };

  return (
    <div className="users-page">
      <div className="users-header">
        <div className="header-top">
          <h1>Users</h1>
          <button className="btn-primary">
            <UserPlus size={16} />
            Add User
          </button>
        </div>

        <div className="users-controls">
          <div className="controls-row">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>

            {/* Order By Controls */}
            <div className="order-controls">
              <div className="order-group">
                <label>Order by:</label>
                <select
                  value={orderBy}
                  onChange={(e) => handleOrderByChange(e.target.value)}
                  className="order-select"
                >
                  <option value="id">ID</option>
                  <option value="createdAt">Created Date</option>
                </select>
              </div>
              <div className="order-group">
                <label>Direction:</label>
                <select
                  value={orderDirection}
                  onChange={(e) => handleOrderDirectionChange(e.target.value)}
                  className="order-select"
                >
                  <option value="DESC">
                    <ChevronDown size={16} /> Descending
                  </option>
                  <option value="ASC">
                    <ChevronUp size={16} /> Ascending
                  </option>
                </select>
              </div>
            </div>

            <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={16} />
              Filter{' '}
              {Object.values(appliedFilters).some((f) => f) && (
                <span className="filter-badge">•</span>
              )}
            </button>
          </div>

          <div className="filter-summary">{getFilterSummary()}</div>

          {showFilters && (
            <div className="filters-dropdown">
              <div className="filters-header">
                <h3>Filters</h3>
                <button className="btn-text" onClick={clearFilters}>
                  Clear All
                </button>
              </div>

              <div className="filter-group">
                <label>User Type:</label>
                <select
                  value={filters.userType}
                  onChange={(e) => handleFilterChange('userType', e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="free">Free</option>
                  <option value="beta">Beta</option>
                  <option value="premium">Premium</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Birth Date Range:</label>
                <div className="date-range">
                  <input
                    type="date"
                    placeholder="From"
                    value={filters.birthdateFrom}
                    onChange={(e) => handleFilterChange('birthdateFrom', e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    placeholder="To"
                    value={filters.birthdateTo}
                    onChange={(e) => handleFilterChange('birthdateTo', e.target.value)}
                  />
                </div>
                <div className="age-category-buttons">
                  <span className="age-label">Quick select:</span>
                  <button type="button" className="age-btn" onClick={() => setAgeCategory('<20')}>
                    &lt;20
                  </button>
                  <button type="button" className="age-btn" onClick={() => setAgeCategory('20-39')}>
                    20-39
                  </button>
                  <button type="button" className="age-btn" onClick={() => setAgeCategory('40-59')}>
                    40-59
                  </button>
                  <button type="button" className="age-btn" onClick={() => setAgeCategory('60+')}>
                    60+
                  </button>
                </div>
              </div>

              <div className="filter-group">
                <label>Have belief on:</label>
                <input
                  type="number"
                  placeholder="Enter Thought ID"
                  value={filters.hasBeliefOnThought}
                  onChange={(e) => handleFilterChange('hasBeliefOnThought', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>Education:</label>
                <select
                  value={filters.education}
                  onChange={(e) => handleFilterChange('education', e.target.value)}
                >
                  <option value="">All Education Levels</option>
                  <option value="High School Graduate">High School Graduate</option>
                  <option value="Associates">Associates</option>
                  <option value="Technical Degree">Technical Degree</option>
                  <option value="Bachelors">Bachelors</option>
                  <option value="Masters">Masters</option>
                  <option value="PhD">PhD</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Political:</label>
                <select
                  value={filters.political}
                  onChange={(e) => handleFilterChange('political', e.target.value)}
                >
                  <option value="">All Political Views</option>
                  <option value="Far Left">Far Left</option>
                  <option value="Slightly Left">Slightly Left</option>
                  <option value="Central">Central</option>
                  <option value="Slightly Right">Slightly Right</option>
                  <option value="Far Right">Far Right</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Sex:</label>
                <select
                  value={filters.sex}
                  onChange={(e) => handleFilterChange('sex', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Country:</label>
                <input
                  type="text"
                  placeholder="Enter Country"
                  value={filters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>State:</label>
                <input
                  type="text"
                  placeholder="Enter State"
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>City:</label>
                <input
                  type="text"
                  placeholder="Enter City"
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>Religion:</label>
                <select
                  value={filters.religion}
                  onChange={(e) => handleFilterChange('religion', e.target.value)}
                >
                  <option value="">All Religions</option>
                  <option value="Christian">Christian</option>
                  <option value="Muslim">Muslim</option>
                  <option value="Hindu">Hindu</option>
                  <option value="Buddhist">Buddhist</option>
                  <option value="Agnostic">Agnostic</option>
                  <option value="Atheist">Atheist</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="filters-actions">
                <button className="btn-secondary" onClick={() => setShowFilters(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={applyFilters}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {selectedUsers.size > 0 && (
            <div className="bulk-actions">
              <span>{selectedUsers.size} selected</span>
              <button className="btn-secondary">
                <Edit2 size={14} />
                Edit
              </button>
              <button className="btn-secondary">
                <Ban size={14} />
                Suspend
              </button>
              <button className="btn-danger">
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="users-content">
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
            <p>Showing mock data instead.</p>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <>
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === users.length && users.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Location</th>
                    <th>Joined</th>
                    <th>Age Category</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.account_id}
                      onClick={(e) => handleRowClick(user.account_id, e)}
                      className="user-row"
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.account_id)}
                          onChange={() => handleSelectUser(user.account_id)}
                        />
                      </td>
                      <td>
                        <div className="user-info">
                          <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                          <span>{user.name}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${getRoleColor(user.type)}`}>{user.type}</span>
                      </td>
                      <td>{user.city || 'N/A'}</td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>{user.ageCategory}</td>
                      <td>
                        <div className="actions">
                          <button className="action-btn">
                            <Edit2 size={14} />
                          </button>
                          <button className="action-btn">
                            <Shield size={14} />
                          </button>
                          <div className="action-dropdown">
                            <button
                              className="action-btn"
                              onClick={(e) => toggleDropdown(user.account_id, e)}
                            >
                              <MoreVertical size={14} />
                            </button>
                            {openDropdown === user.account_id && (
                              <div className="dropdown-menu">
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleLoginAsUserWeb(user.account_id)}
                                >
                                  <Monitor size={16} />
                                  <span>Login as User: Web</span>
                                </button>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleLoginAsUserMobile(user.account_id)}
                                >
                                  <Smartphone size={16} />
                                  <span>Login as User: Mobile</span>
                                </button>
                                <div className="dropdown-divider"></div>
                                <button className="dropdown-item dropdown-item-danger">
                                  <Trash2 size={16} />
                                  <span>Delete User</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">Showing {users.length} users</div>
              <div className="pagination-controls">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button onClick={handleNextPage} className="btn-secondary">
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
